  import React, { useState, useEffect, useMemo } from "react";
  import { useForm, useFieldArray } from "react-hook-form";
  import moment from "moment";
  import { useNavigate, useLocation } from "react-router-dom";
  import axios from "axios";
  import Sidebar from "./Sidebar";
  import DashboardLoader from "./loader/DashboardLoader.js";

  const VideoForm = () => {
    const { register, handleSubmit, control, reset, watch, setValue, formState: { errors: formErrors } } = useForm({
      defaultValues: {
        topic: "",
        phases: [],
        month: "",
        year: "",
        mode: "onBlur"
      },
    });
    const navigate = useNavigate();
    const location = useLocation();
    const existingData = location.state?.existingData;
    const isUpdateMode = !!existingData;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "phases",
    });

    const [errors, setErrors] = useState({});
    const [videoTypes, setVideoTypes] = useState([]);
    const [activeSidebarItem, setActiveSidebarItem] = useState(
      isUpdateMode ? "videos" : "add",
    );
    const [storyType, setStoryType] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isShortVideo, setIsShortVideo] = useState(false);
    const [initialFormSetupDone, setInitialFormSetupDone] = useState(false);
    
    const currentMonth = moment().month();
    const currentDay = moment().date();
    // Change Cutoff date here
    const deadlineDay = 20;

    // Watch form fields
    const selectedMonth = watch("month");
    const selectedYear = watch("year");
    const selectedStoryType = watch("storyType");
    const phases = watch("phases");
    
    const currentYear = moment().year();

    // Simplified phases
    const phasesEnum = [
      "TEAM_SELECTION_WORK_DELEGATION",
      "SCRIPT_FINALIZATION",
      "RECORDING",
      "EDITING",
      "TITLE_DESCRIPTION_HASHTAG_THUMB",
      "REVIEW",
      "PUBLISH"
    ];

    // Phase display names for better UI presentation
    const phaseDisplayNames = {
      "TEAM_SELECTION_WORK_DELEGATION": "Team Selection & Work Delegation",
      "SCRIPT_FINALIZATION": "Script Finalization",
      "RECORDING": "Recording",
      "EDITING": "Editing",
      "TITLE_DESCRIPTION_HASHTAG_THUMB": "Title, Description, Hashtag, & Thumb",
      "REVIEW": "Review",
      "PUBLISH": "Publish"
    };

    // Memoize the months array to prevent unnecessary recalculations
    const months = useMemo(() => {
      return moment.months().map((month, index) => {
        if (selectedYear > currentYear) {
          return month.toUpperCase();
        } else if (
          (index === currentMonth + 1 && currentDay <= deadlineDay) ||
          index > currentMonth + 1
        ) {
          return month.toUpperCase();
        }
        return null;
      }).filter(Boolean);
    }, [selectedYear, currentYear, currentMonth, currentDay, deadlineDay]);
    
    const years = useMemo(() => {
      return Array.from({ length: 10 }, (_, i) => moment().year() + i);
    }, []);

    // Calculate date ranges for the form based on selected month
    const { startDate, endDate, selectedMonthStartDate } = useMemo(() => {
      if (selectedMonth && selectedYear) {
        const monthIndex = moment().month(selectedMonth.toLowerCase()).month();
        
        // Start date is 1 month before the selected month
        const startDate = moment()
          .year(selectedYear)
          .month(monthIndex)
          .subtract(1, "month")
          .startOf("month");

        // End date is end of selected month
        const endDate = moment()
          .year(selectedYear)
          .month(monthIndex)
          .endOf("month");

        // Selected month start date (for Publish phase)
        const selectedMonthStartDate = moment()
          .year(selectedYear)
          .month(monthIndex)
          .startOf("month");

        return { startDate, endDate, selectedMonthStartDate };
      }
      
      return { startDate: null, endDate: null, selectedMonthStartDate: null };
    }, [selectedMonth, selectedYear]);

    // Get min date for a specific phase based on previous phase selection
    const getPhaseMinDate = (phaseIndex) => {
      // For the first phase, use the overall start date (previous month)
      if (phaseIndex === 0) {
        return startDate?.format("YYYY-MM-DD");
      }
      
      // For other phases, use previous phase's date if available
      if (phases?.[phaseIndex - 1]?.startDate) {
        return phases[phaseIndex - 1].startDate;
      }
      
      return startDate?.format("YYYY-MM-DD");
    };

    const validateDates = (phases) => {
      let validationErrors = {};

      // If short video (0-90s), only validate the publish phase
      if (isShortVideo) {
        // Find the publish phase
        const publishPhase = phases.find(phase => phase.name === "PUBLISH");
        
        if (!publishPhase) {
          validationErrors.phases = "Publish phase is required";
          return validationErrors;
        }
        
        if (!publishPhase.startDate) {
          validationErrors["phases.6.startDate"] = "Publish date is required";
        } else if (selectedMonthStartDate) {
          // Verify publish date is within selected month
          const startMoment = moment(publishPhase.startDate);
          if (startMoment.isBefore(selectedMonthStartDate)) {
            validationErrors["phases.6.startDate"] = "Publish must be in the selected month";
          }
          
          const monthEnd = moment(selectedMonthStartDate).endOf('month');
          if (startMoment.isAfter(monthEnd)) {
            validationErrors["phases.6.startDate"] = "Publish date cannot be after the selected month";
          }
        }
        
        return validationErrors;
      }

      // Regular validation for all phases
      // Check if all phases are present
      if (!phases || phases.length !== phasesEnum.length) {
        validationErrors.phases = "All phases must be configured";
        return validationErrors;
      }

      let lastDate = null;

      phases.forEach((phase, index) => {
        const { startDate, endDate, name } = phase;

        // All phases require start date
        if (!startDate) {
          validationErrors[`phases.${index}.startDate`] = "Start Date is required";
        } else {
          // Ensure phases are sequential
          if (lastDate && moment(startDate).isBefore(moment(lastDate))) {
            validationErrors[`phases.${index}.startDate`] = `Must be after ${moment(lastDate).format("DD.MM.YYYY")}`;
          }
          
          // For publish phase, verify it's within the selected month
          
          
          // Update lastDate for next phase check
          lastDate = startDate;
        }

        // Only editing phase requires end date
        if (name === "EDITING") {
          if (!endDate) {
            validationErrors[`phases.${index}.endDate`] = "End Date is required for Editing phase";
          } else if (startDate && endDate) {
            // Verify enddate is greater than startdate
            const startMoment = moment(startDate);
            const endMoment = moment(endDate);
            
            if (startMoment.isBefore(endMoment)) {
              validationErrors[`phases.${index}.endDate`] = "End Date must be equal to or greater than Start Date";
            }
          }
        }

        // Additional date validations
        if (startDate && selectedMonth && selectedYear) {
          const startMoment = moment(startDate);
          const monthEnd = moment(selectedMonthStartDate).endOf('month');
          
          if (startMoment.isAfter(monthEnd)) {
            validationErrors[`phases.${index}.startDate`] = "Start Date cannot be after the selected month";
          }
          
          if (name === "EDITING" && endDate) {
            const endMoment = moment(endDate);
            if (endMoment.isAfter(monthEnd)) {
              validationErrors[`phases.${index}.endDate`] = "End Date cannot be after the selected month";
            }
          }
        }
      });

      return validationErrors;
    };
    
    const handleSidebarItemClick = (itemId, path) => {
      navigate(path); 
      setActiveSidebarItem(itemId);
    };

    // Function to save the last selections to localStorage
    const saveLastSelections = (data) => {
      const selectionsToSave = {
        year: data.year,
        month: data.month,
        videoStyle: data.videoStyle,
        storyType: data.storyType
      };
      localStorage.setItem('lastVideoFormSelections', JSON.stringify(selectionsToSave));
    };

    const onSubmit = async (data) => {
      try {
        setLoading(true);
        const validationErrors = validateDates(data.phases);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          setLoading(false);
          return;
        }

        // If it's a short video, keep only the publish phase
        let formattedPhases = data.phases;
        if (isShortVideo) {
          formattedPhases = [data.phases.find(phase => phase.name === "PUBLISH")];
        }

        const formattedData = {
          month: data.month,
          year: data.year,
          videos: {
            topic: data?.topic,
            phases: formattedPhases,
            status: "PENDING_APPROVAL",
            videoStyle: data?.videoStyle,
            storyType: JSON.parse(data?.storyType).id,
          },
        };

        // Save selections before submission
        saveLastSelections(data);

        if (isUpdateMode) {
          formattedData.videoId = existingData._id;
          formattedData.planId = existingData.planId;
          
          await axios.put(
            `${process.env.REACT_APP_API_URL}/update-video`,
            formattedData,
            { withCredentials: true },
          );
        } else {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/create-plan`,
            formattedData,
            { withCredentials: true },
          );
        }

        reset();
        setLoading(false);
        setErrors({});
      } catch (err) {
        setLoading(false);
        console.log(err);
      }
    };

    const displayYears = useMemo(() => {
      return years?.includes(existingData?.year)
        ? years
        : [...years, existingData?.year].filter(Boolean);
    }, [years, existingData?.year]);

    // Function to load last selections from localStorage
    const loadLastSelections = () => {
      try {
        const savedSelections = localStorage.getItem('lastVideoFormSelections');
        if (savedSelections) {
          return JSON.parse(savedSelections);
        }
      } catch (err) {
        console.log("Error loading saved selections:", err);
      }
      return null;
    };

    // Fetch video types and story types
    useEffect(() => {
      const getVideoTypes = async () => {
        try {
          const videoTypesRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/get-video-styles`,
            {
              withCredentials: true,
            },
          );
          setVideoTypes(videoTypesRes.data);
    
          const storyTypesRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/get-story-types`,
            {
              withCredentials: true,
            },
          );
          setStoryType(storyTypesRes.data);
        } catch (err) {
          console.log(err);
        }
      };
      
      getVideoTypes();
    }, []);

    // Initialize empty phases once when component mounts
    useEffect(() => {
      if (!initialFormSetupDone) {
        phasesEnum.forEach((phase, index) => {
          setValue(`phases.${index}.name`, phase);
        });
      }
    }, [setValue, initialFormSetupDone, phasesEnum]);

    // Initialize the form with existing data or last selections
    useEffect(() => {
      if (!initialFormSetupDone && videoTypes.length > 0 && storyType.length > 0) {
        if (existingData) {
          // If we have existing data (update mode), use that
          setValue("topic", existingData.videos?.topic || "");
          setValue("month", existingData.month || "");
          setValue("year", existingData.year ? String(existingData.year) : "");
          setValue("videoStyle", existingData?.videoStyle?._id || "");
          
          // Handle story type with validation
          if (existingData?.storyType?._id) {
            setValue(
              "storyType",
              JSON.stringify({
                id: existingData.storyType._id,
                name: existingData.storyType.name,
              })
            );
            
            // Check if existing data is a short video
            const existingStoryType = storyType.find(type => type._id === existingData.storyType._id);
            if (existingStoryType && (existingStoryType.durationRange === "0-90s" || existingStoryType.durationRange === "91s-3m")) {
              setIsShortVideo(true);
            }
          }

          // Initialize phases with the new structure
          if (existingData.videos?.phases) {
            // Map old phases to new simplified structure
            const oldPhases = existingData.videos.phases;
            
            // Handle team selection & work delegation (combine first two phases)
            if (oldPhases[0] || oldPhases[1]) {
              setValue(`phases.0.name`, "TEAM_SELECTION_WORK_DELEGATION");
              setValue(`phases.0.startDate`, oldPhases[0]?.startDate ? moment(oldPhases[0].startDate).format("YYYY-MM-DD") : "");
            }
            
            // Map remaining phases
            setValue(`phases.1.name`, "SCRIPT_FINALIZATION");
            setValue(`phases.1.startDate`, oldPhases[2]?.startDate ? moment(oldPhases[2].startDate).format("YYYY-MM-DD") : "");
            
            setValue(`phases.2.name`, "RECORDING");
            setValue(`phases.2.startDate`, oldPhases[3]?.startDate ? moment(oldPhases[3].startDate).format("YYYY-MM-DD") : "");
            
            setValue(`phases.3.name`, "EDITING");
            setValue(`phases.3.startDate`, oldPhases[4]?.startDate ? moment(oldPhases[4].startDate).format("YYYY-MM-DD") : "");
            setValue(`phases.3.endDate`, oldPhases[4]?.endDate ? moment(oldPhases[4].endDate).format("YYYY-MM-DD") : "");
            
            // Combine title selection, description, hashtags and thumbnail phases
            setValue(`phases.4.name`, "TITLE_DESCRIPTION_HASHTAG_THUMB");
            setValue(`phases.4.startDate`, oldPhases[7]?.startDate ? moment(oldPhases[7].startDate).format("YYYY-MM-DD") : "");
            
            setValue(`phases.5.name`, "REVIEW");
            setValue(`phases.5.startDate`, oldPhases[11]?.startDate ? moment(oldPhases[11].startDate).format("YYYY-MM-DD") : "");
            
            setValue(`phases.6.name`, "PUBLISH");
            setValue(`phases.6.startDate`, oldPhases[12]?.startDate ? moment(oldPhases[12].startDate).format("YYYY-MM-DD") : "");
          }
        } else if (!isUpdateMode) {
          // If we're not in update mode, check for saved selections
          const lastSelections = loadLastSelections();
          if (lastSelections) {
            // Only set values that are valid
            if (lastSelections.year && displayYears.includes(Number(lastSelections.year))) {
              setValue("year", lastSelections.year);
            }
            
            if (lastSelections.month && months.includes(lastSelections.month)) {
              setValue("month", lastSelections.month);
            }
            
            if (lastSelections.videoStyle && videoTypes.some(type => type._id === lastSelections.videoStyle)) {
              setValue("videoStyle", lastSelections.videoStyle);
            }
            
            if (lastSelections.storyType) {
              try {
                const parsed = JSON.parse(lastSelections.storyType);
                // Check if the story type still exists
                if (storyType.some(type => type._id === parsed.id)) {
                  setValue("storyType", lastSelections.storyType);
                  
                  // Check if it's a short video
                  const selectedType = storyType.find(type => type._id === parsed.id);
                  if (selectedType && selectedType.durationRange === "0-90s" || selectedType.durationRange === "91s-3m") {
                    setIsShortVideo(true);
                  }
                }
              } catch (err) {
                console.log("Error parsing saved storyType:", err);
              }
            }
          }
        }
        
        // Mark initialization as done to prevent re-runs
        setInitialFormSetupDone(true);
      }
    }, [existingData, setValue, videoTypes, storyType, displayYears, months, isUpdateMode, initialFormSetupDone]);

    // Watch for story type changes and update isShortVideo accordingly
    // This effect should run separately from the initialization effect
    useEffect(() => {
      if (selectedStoryType && storyType.length > 0) {
        try {
          const parsedStoryType = JSON.parse(selectedStoryType);
          const selectedType = storyType.find(type => type._id === parsedStoryType.id);
          
          setIsShortVideo(selectedType && selectedType.durationRange === "0-90s" || selectedType.durationRange === "91s-3m");
        } catch (err) {
          console.log("Error parsing selected story type:", err);
          setIsShortVideo(false);
        }
      }
    }, [selectedStoryType, storyType]);
  
  return (
     
      

    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        activeSidebarItem={activeSidebarItem}
        onSidebarItemClick={handleSidebarItemClick}
      />
{loading ? 
     <div className="flex-1 flex items-center justify-center">

     <DashboardLoader /> 
     </div>:
      <div className="flex-1">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-blue-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">
              {isUpdateMode ? "Update Video Plan" : "Create New Video Plan"}
            </h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Video Topic
                </label>
                <input
                  {...register("topic", { required: "Video topic is required" })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter the video topic"
                />
                {formErrors.topic && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.topic.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Plan Year
                </label>
                <select
                  {...register("year", { required: "Year is required" })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Year</option>
                  {displayYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {formErrors.year && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.year.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Plan Month
                </label>
                <select
                  {...register("month", { required: "Month is required" })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Month</option>
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
                {formErrors.month && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.month.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="storyType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Story Type
                </label>
                
                <select
                  id="storyType"
                  {...register("storyType", { required: "Story type is required" })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  {Array.isArray(storyType) &&
                    storyType.map((type) => (
                      <option
                        key={type._id}
                        value={JSON.stringify({
                          id: type._id,
                          name: type.name,
                        })}
                      >
                        {type.name} - {type.durationRange}
                      </option>
                    ))}
                </select>
                {formErrors.storyType && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.storyType.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="videoType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Video Style
                </label>
                
                <select
                  id="videoType"
                  {...register("videoStyle", { required: "Video style is required" })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  {Array.isArray(videoTypes) &&
                    videoTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name} - {type.effortLevel}
                      </option>
                    ))}
                </select>
                {formErrors.videoStyle && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.videoStyle.message}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Video Phases
              </h2>
              
              {isShortVideo && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-300 rounded-md">
                  <p className="text-sm text-blue-600">
                    <strong>Note:</strong> For short videos (0-90s) and (90s-3m), only the Publish phase is required.
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phase
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        End Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {phasesEnum.map((phase, index) => {
                      // If short video, display only the publish phase
                      if (isShortVideo && phase !== "PUBLISH") {
                        return null;
                      }
                      
                      return (
                        <tr key={phase} className={Object.keys(errors).some(key => key.startsWith(`phases.${index}`)) ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input type="hidden" {...register(`phases.${index}.name`)} value={phase} />
                            <div className="text-sm font-medium text-gray-900">
                              {phaseDisplayNames[phase]}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="date"
                              {...register(`phases.${index}.startDate`, {
                                required: "Start date is required"
                              })}
                              className={`w-full px-3 py-2 bg-white border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                errors[`phases.${index}.startDate`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                              min={isShortVideo ? selectedMonthStartDate?.format("YYYY-MM-DD") : getPhaseMinDate(index)}
                              max={endDate?.format("YYYY-MM-DD")}
                            />
                            {(formErrors.phases?.[index]?.startDate || errors[`phases.${index}.startDate`]) && (
                              <p className="mt-1 text-xs text-red-600">
                                {formErrors.phases?.[index]?.startDate?.message || errors[`phases.${index}.startDate`]}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {/* Only show end date field for the Editing phase and not for short videos */}
                            {phase === "EDITING" && !isShortVideo ? (
                              <>
                                <input
                                  type="date"
                                  {...register(`phases.${index}.endDate`, {
                                    required: "End date is required for Editing phase"
                                  })}
                                  className={`w-full px-3 py-2 bg-white border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                    errors[`phases.${index}.endDate`] ? 'border-red-300' : 'border-gray-300'
                                  }`}
                                  min={phases?.[index]?.startDate || getPhaseMinDate(index)}
                                  max={endDate?.format("YYYY-MM-DD")}
                                />
                                {(formErrors.phases?.[index]?.endDate || errors[`phases.${index}.endDate`]) && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {formErrors.phases?.[index]?.endDate?.message || errors[`phases.${index}.endDate`]}
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">Not required</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isUpdateMode ? "Update Video Plan" : "Create Video Plan"}
              </button>
            </div>
          </form>
        </div>
      </div>
}
    </div>
                  
  );
};

export default VideoForm;